import React, { useEffect, useState } from 'react';
import { Box, Checkbox, Grid } from '@mui/material';
import {
  MenuTemplateGeneralFormInputs,
  MenuTemplatePartnerLinkConfiguration,
} from 'modules/aftermarketMenuConstructor/types';
import { AutocompleteField, InputField, SwitchField } from 'components/form';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { SelectOption } from 'global';
import { useAftermarketPermissions } from 'modules/aftermarketMenuConstructor/hooks/useAftermarketPermissions';
import { menuConstructorActions, menuConstructorSelectors } from 'modules/aftermarketMenuConstructor/model';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { keyBy } from 'utils/arrayUtils';
import { shareMenuSelectAllOption } from 'modules/aftermarketMenuConstructor/constants';
import { format, subDays } from 'date-fns';
import { PartnerProfileStatuses } from '@trnsact/trnsact-shared-types';
import { GET_PARTNER_LINKS_LIGHTWEIGHT } from 'modules/aftermarketPartner/api';
import { PartnerLinksLightweightResponse } from 'modules/aftermarketPartner/api/types';
import _ from 'lodash';

export const GeneralInformationFormAftermarketPartner = () => {
  const dispatch = useDispatch();

  const account = useSelector((state: any) => state.account);

  const { hasWriteAccess } = useAftermarketPermissions();

  const [dealersOptions, setDealersOptions] = useState<SelectOption[]>([]);

  const { loading: loadingPartnerLinks } = useQuery<PartnerLinksLightweightResponse>(GET_PARTNER_LINKS_LIGHTWEIGHT, {
    skip: !account.dynamicsAccountId,
    variables: {
      dynamicsId: account.dynamicsAccountId,
    },
    onCompleted(data) {
      const dealersOptions: SelectOption[] = _.uniqBy(data.getPartnerLinksLightweight, 'dynamicsPartnerAccountId').map(
        partnerLink => ({
          value: partnerLink.partnerLinkId!,
          label: partnerLink.accountName!,
        })
      );
      setDealersOptions(dealersOptions);
      dispatch(menuConstructorActions.setPartnerLinksById(keyBy(data.getPartnerLinksLightweight, 'partnerLinkId')));
    },
  });

  const {
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useFormContext<MenuTemplateGeneralFormInputs>();

  const { fields: linkedToCriteriasFields, replace: replaceLinkedToCriterias } = useFieldArray({
    control,
    name: 'linkedToCriterias',
  });

  useEffect(() => {
    const subscription = watch(value => {
      dispatch(menuConstructorActions.setMenuGeneralFormValues(value as MenuTemplateGeneralFormInputs));
    });

    return subscription.unsubscribe;
  }, [watch]);

  const handleChangeDealers = (newSelected: SelectOption[]) => {
    const partnerLinksIds = newSelected.map(o => o.value);

    const linkedToCriterias = getValues('linkedToCriterias');
    const linkedToCriteriasById = keyBy(linkedToCriterias, 'partnerLinkId');

    const linkedToCriteriasToSet = partnerLinksIds.reduce<MenuTemplatePartnerLinkConfiguration[]>(
      (acc, selectedPartnerLinkId) => {
        if (linkedToCriteriasById[selectedPartnerLinkId]) {
          acc.push(linkedToCriteriasById[selectedPartnerLinkId]);
        } else {
          acc.push({ partnerLinkId: selectedPartnerLinkId, isReadOnly: true });
        }
        return acc;
      },
      []
    );
    setValue('partnerLinksToShareMenu', newSelected);
    replaceLinkedToCriterias(linkedToCriteriasToSet);
  };

  const handleSelectAll = () => {
    getValues('partnerLinksToShareMenu').length === dealersOptions.length
      ? handleChangeDealers([])
      : handleChangeDealers(dealersOptions);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <InputField
          label="Name"
          control={control}
          name="name"
          rules={{
            required: true,
          }}
          inputProps={{
            required: true,
            error: !!errors.name,
            helperText: errors.name && 'Required',
            disabled: !hasWriteAccess,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <InputField
          label="Description"
          control={control}
          name="description"
          inputProps={{ disabled: !hasWriteAccess }}
        />
      </Grid>
      <Grid item xs={12}>
        <AutocompleteField<any, true, MenuTemplateGeneralFormInputs>
          multiple
          limitTags={8}
          control={control}
          disableCloseOnSelect
          name="partnerLinksToShareMenu"
          options={[shareMenuSelectAllOption, ...dealersOptions]}
          textFieldProps={{ label: 'Dealers to share the menu with' }}
          disabled={loadingPartnerLinks || !dealersOptions.length}
          extraAction={handleChangeDealers}
          isOptionEqualToValue={(option, value) => option.value === value?.value}
          renderOption={(props, option, { selected }, ownerState) => {
            const { key, ...optionProps } = props;
            return option.value === shareMenuSelectAllOption.value ? (
              <Box component="li" key={key} {...optionProps} onClick={handleSelectAll}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={ownerState.value?.length === dealersOptions.length}
                />
                {option.label}
              </Box>
            ) : (
              <Box component="li" key={key} {...optionProps}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.label}
              </Box>
            );
          }}
        />
      </Grid>

      {/*       {linkedToCriteriasFields?.map((linkingCriteria, idx) => (
        <Grid key={linkingCriteria.partnerLinkId} item container spacing={1} xs={12}>
          <Grid item container xs={3} alignItems="center">
            <Typography>{partnerLinksById[linkingCriteria.partnerLinkId]?.accountName}</Typography>
          </Grid>
          <Grid item container xs={8} alignItems="center">
            <SwitchField
              control={control}
              name={`linkedToCriterias.${idx}.isReadOnly`}
              label="Share in Read-Only mode"
              disabled
            />
          </Grid>
        </Grid>
      ))} */}
    </Grid>
  );
};
